import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { IdentityService } from 'src/app/@core/services/identity.service';

@Injectable({
    providedIn: 'root',
})

export class SwalService {

    constructor(private translateService: TranslateService, private identityService: IdentityService) {
    }

    /**
     * Unified Code mode (DGA criterion 19): modals use the uc-modal structure (header / body / actions),
     * primary + secondary action buttons and a labelled close button instead of the SweetAlert2 colours.
     * Custom mode passes the options through unchanged.
     */
    private fire(options: SweetAlertOptions) {
        if (!this.identityService.isUnifiedCode()) {
            return Swal.fire(options);
        }
        const { confirmButtonColor, cancelButtonColor, denyButtonColor, ...rest } = options;
        return Swal.fire({
            ...rest,
            buttonsStyling: false,
            showCloseButton: true,
            closeButtonAriaLabel: this.translateService.instant('unifiedComponents.close'),
            customClass: {
                popup: 'uc-modal',
                title: 'uc-modal-title',
                htmlContainer: 'uc-modal-body',
                actions: 'uc-modal-actions',
                closeButton: 'uc-modal-close',
                icon: 'uc-modal-icon',
                confirmButton: 'uc-btn-primary',
                denyButton: 'uc-btn-secondary',
                cancelButton: 'uc-btn-secondary',
            },
        });
    }

    deleteConfirmation() {
        return this.fire({
            title: this.translateService.instant('actions.delete_title'),
            text: this.translateService.instant('actions.delete_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.translateService.instant('actions.delete_confirm'),
            cancelButtonText: this.translateService.instant('actions.cancel'),
        });
    }

    customConfirmation(title: string, bodyText: string,
        confirmButtonText: string = this.translateService.instant('actions.confirm')) {
        return this.fire({
            title: title,
            text: bodyText,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: confirmButtonText,
            cancelButtonText: this.translateService.instant('actions.cancel'),
        });
    }

    deleted() {
        return this.fire({
            title: this.translateService.instant('actions.deleted_title'),
            text: this.translateService.instant('actions.deleted_text'),
            icon: 'success',
        });
    }

    saved() {
        return this.fire({
            title: this.translateService.instant('actions.success_title'),
            text: this.translateService.instant('actions.success_text'),
            icon: 'success',
        });
    }

    error(message: string = null) {
        return this.fire({
            title: message ?? this.translateService.instant('actions.error_title'),
            text: this.translateService.instant('actions.error_text'),
            icon: 'error',
        });
    }

    errorWithKey(message: string = null, title: string = null) {
        return this.fire({
            title: this.translateService.instant(title ?? 'actions.error_title'),
            text: this.translateService.instant(message ?? 'actions.error_text'),
            icon: 'error',
        });
    }

    forceDelete(title: string, confirmBtnTxt: string, denyBtnTxt: string) {
        return this.fire({
            title: title,
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: confirmBtnTxt,
            denyButtonText: denyBtnTxt,
        });
    }

    fireMessage(title: string, text: string, status: string) {
        return this.fire({
            title,
            text,
            icon: status,
            confirmButtonColor: '#3085d6',
            confirmButtonText: this.translateService.instant('actions.cancel'),
        } as SweetAlertOptions);
    }
}
